/**
 * scriptDiff.ts — ADR-084 D6 / design doc "Save-and-activate flow" item 2:
 * a `scripts[]` diff keyed on `product_script_key` then `step_id`, never
 * array index, so a silent reorder (D4 carries no ordering rule) renders as
 * a position change per step instead of a delete+add pair. Replaces the raw
 * per-script `JSON.stringify` row `promptTemplateDiff.ts` used before the
 * Scripts tab existed (S3a) — D3's payload is field-level old→new already;
 * this is rendering-only (design doc open question 2).
 */

import type { Script, Step } from '../../api/types';
import type { VersionDiffRow } from './promptTemplateDiff';

const STEP_FIELDS: (keyof Step)[] = [
  'goal',
  'enabled',
  'exit_when',
  'required_facts',
  'allowed_tools',
  'on_decline',
  'on_ineligible',
  'on_consent_not_classified',
  'retry_guidance',
  'advance_when',
  'lead_status',
];

const fieldValue = (step: Step, field: keyof Step): string => {
  // absent (older versions) reads as null, same as an explicit null
  const value = step[field] ?? null;
  return typeof value === 'string' ? value : JSON.stringify(value);
};

const diffSteps = (scriptKey: string, before: Step[], after: Step[]): VersionDiffRow[] => {
  const rows: VersionDiffRow[] = [];
  const beforeIndex = new Map(before.map((step, i) => [step.step_id, i]));
  const afterIndex = new Map(after.map((step, i) => [step.step_id, i]));
  const beforeById = new Map(before.map((step) => [step.step_id, step]));
  const afterById = new Map(after.map((step) => [step.step_id, step]));

  new Set([...beforeIndex.keys(), ...afterIndex.keys()]).forEach((stepId) => {
    const prefix = `agent_config.scripts.${scriptKey}.steps.${stepId}`;
    const beforeStep = beforeById.get(stepId);
    const afterStep = afterById.get(stepId);

    if (beforeStep == null && afterStep != null) {
      rows.push({ path: prefix, before: null, after: `+ ${afterStep.goal || stepId}` });
    } else if (beforeStep != null && afterStep == null) {
      rows.push({ path: prefix, before: `− ${beforeStep.goal || stepId}`, after: null });
    } else if (beforeStep != null && afterStep != null) {
      const oldPos = beforeIndex.get(stepId)!;
      const newPos = afterIndex.get(stepId)!;
      if (oldPos !== newPos) {
        rows.push({
          path: `${prefix}.position`,
          before: String(oldPos + 1),
          after: String(newPos + 1),
        });
      }
      STEP_FIELDS.forEach((field) => {
        const beforeValue = fieldValue(beforeStep, field);
        const afterValue = fieldValue(afterStep, field);
        if (beforeValue !== afterValue) {
          rows.push({ path: `${prefix}.${field}`, before: beforeValue, after: afterValue });
        }
      });
    }
  });

  return rows;
};

const SCRIPT_META_FIELDS: (keyof Script)[] = ['kind', 'version', 'product_code', 'display_name'];

/** Field-level diff of `scripts[]`, keyed on `product_script_key` then `step_id` — never array index. */
export const diffScripts = (
  before: Script[] | undefined,
  after: Script[] | undefined,
): VersionDiffRow[] => {
  const rows: VersionDiffRow[] = [];
  const beforeByKey = new Map((before ?? []).map((s) => [s.product_script_key, s]));
  const afterByKey = new Map((after ?? []).map((s) => [s.product_script_key, s]));

  new Set([...beforeByKey.keys(), ...afterByKey.keys()]).forEach((key) => {
    const beforeScript = beforeByKey.get(key);
    const afterScript = afterByKey.get(key);

    if (beforeScript == null && afterScript != null) {
      rows.push({
        path: `agent_config.scripts.${key}`,
        before: null,
        after: `+ ${afterScript.display_name}`,
      });
    } else if (beforeScript != null && afterScript == null) {
      rows.push({
        path: `agent_config.scripts.${key}`,
        before: `− ${beforeScript.display_name}`,
        after: null,
      });
    } else if (beforeScript != null && afterScript != null) {
      SCRIPT_META_FIELDS.forEach((field) => {
        const beforeValue = beforeScript[field];
        const afterValue = afterScript[field];
        const beforeStr = beforeValue == null ? null : String(beforeValue);
        const afterStr = afterValue == null ? null : String(afterValue);
        if (beforeStr !== afterStr) {
          rows.push({
            path: `agent_config.scripts.${key}.${field}`,
            before: beforeStr,
            after: afterStr,
          });
        }
      });
      rows.push(...diffSteps(key, beforeScript.steps, afterScript.steps));
    }
  });

  return rows;
};
